import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import { eur } from "@/lib/format";
import { Wrench, Save, AlertTriangle, Search, CheckSquare, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";

/**
 * HK-Pflege: Liste der Bewegungen eines Mandanten + Zeitraums.
 * HK Kal und HK Ist sind direkt inline editierbar; alle Änderungen werden
 * als Bulk-Patch an /movements/hk-bulk gesendet.
 */
export default function HkPflege() {
    const { activeTenant } = useTenant();
    const { zeitraum, setZeitraum } = usePeriod();
    const { user } = useAuth();
    const isGuest = user?.role === "guest";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [onlyMissing, setOnlyMissing] = useState(true);
    // Dirty-Map: { [movement_id]: { hk_ist?: number|null, hk_kal?: number|null } }
    const [dirty, setDirty] = useState({});
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", expiresInSec: 600 });

    const load = async () => {
        if (!activeTenant) return;
        setLoading(true);
        try {
            const params = { tenant_id: activeTenant.tenant_id, zeitraum, nur_aktive: true };
            const r = await api.get("/movements", { params });
            setRows(r.data);
            setDirty({});
        } catch {
            toast.error("Bewegungen konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id, zeitraum]);

    const filtered = useMemo(() => {
        let list = rows;
        const hasSearch = search.trim().length > 0;
        // Wenn aktiv gesucht wird, ignorieren wir den „Nur ohne HK"-Filter,
        // damit man jeden Titel finden und korrigieren kann.
        if (onlyMissing && !hasSearch) {
            list = list.filter(
                (m) => (!m.hk_ist || m.hk_ist === 0) && (!m.hk_kal || m.hk_kal === 0),
            );
        }
        if (hasSearch) {
            const q = search.toLowerCase().trim();
            const qNorm = q.replace(/[^0-9x]/g, "");
            list = list.filter((m) => {
                const isbnRaw = (m.isbn || "").toLowerCase();
                const isbnNorm = (m.isbn_normalized || "").toLowerCase();
                return (
                    isbnRaw.includes(q) ||
                    (qNorm && isbnNorm.includes(qNorm)) ||
                    (m.haupttitel || "").toLowerCase().includes(q) ||
                    (m.erloesgruppe || "").toLowerCase().includes(q)
                );
            });
        }
        // Sortierung nach ISBN — über isbn_normalized, damit Bindestriche egal sind
        const sorted = [...list].sort((a, b) => {
            const an = (a.isbn_normalized || (a.isbn || "").replace(/[^0-9X]/gi, "")).toUpperCase();
            const bn = (b.isbn_normalized || (b.isbn || "").replace(/[^0-9X]/gi, "")).toUpperCase();
            return an.localeCompare(bn);
        });
        return sorted;
    }, [rows, onlyMissing, search]);

    const formatEt = (m) => {
        if (!m.et) return "—";
        try {
            return new Date(m.et).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
        } catch {
            return m.et;
        }
    };

    // Hilfsfunktion: Roh-String → number|null
    const parseHk = (raw) => {
        if (raw === null || raw === undefined) return null;
        const s = String(raw).replace(",", ".").trim();
        if (s === "" || s === "." || s === "-") return null;
        const n = Number(s);
        if (Number.isNaN(n)) return null;
        // Auf 4 Nachkommastellen runden, damit kein FP-Drift entsteht
        return Math.round(n * 10000) / 10000;
    };

    const setVal = (id, field, raw) => {
        // Erlaubt sind nur Ziffern + ein Trenner (, oder .) + optional Minus
        const normalized = String(raw).replace(",", ".");
        if (raw !== "" && !/^-?\d*\.?\d*$/.test(normalized)) return;
        setDirty((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [field]: raw },
        }));
    };

    const getDisplayVal = (m, field) => {
        if (dirty[m.id] && dirty[m.id][field] !== undefined) {
            // Roh-String 1:1 zurückgeben — der User soll sehen, was er tippt
            return String(dirty[m.id][field] ?? "");
        }
        const orig = m[field];
        return orig ? String(orig).replace(".", ",") : "";
    };

    // Effektiver HK = HK Ist falls > 0, sonst HK Kal (gleiche Logik wie Backend)
    const getEffectiveHk = (m) => {
        const d = dirty[m.id] || {};
        const hk_ist = d.hk_ist !== undefined ? parseHk(d.hk_ist) : m.hk_ist;
        const hk_kal = d.hk_kal !== undefined ? parseHk(d.hk_kal) : m.hk_kal;
        if (hk_ist && hk_ist > 0) return hk_ist;
        if (hk_kal && hk_kal > 0) return hk_kal;
        return null;
    };

    const getPreviewLagerwert = (m) => {
        const hk = getEffectiveHk(m);
        if (hk === null) return null;
        const bestand = (m.lager_oe || 0) + (m.lager_de || 0);
        return Math.round(bestand * hk * 100) / 100;
    };

    const dirtyCount = Object.keys(dirty).length;

    const toggleConfirmNull = async (id, checked) => {
        // Optimistisches Update auf der aktuell geladenen Liste
        setRows((prev) =>
            prev.map((m) => (m.id === id ? { ...m, hk_confirmed_null: checked } : m)),
        );
        try {
            await api.patch("/movements/hk-confirm-null", {
                tenant_id: activeTenant.tenant_id,
                movement_ids: [id],
                confirmed: checked,
            });
            toast.success(
                checked
                    ? 'HK bewusst leer bestätigt'
                    : 'Bestätigung HK bewusst leer aufgehoben',
            );
        } catch (e) {
            // Rollback bei Fehler
            setRows((prev) =>
                prev.map((m) => (m.id === id ? { ...m, hk_confirmed_null: !checked } : m)),
            );
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    /** Massenaktion: alle sichtbaren Titel ohne HK als „bewusst leer" bestätigen. */
    const bulkConfirmVisible = async () => {
        const candidates = filtered.filter(
            (m) =>
                !m.hk_confirmed_null &&
                (!m.hk_ist || m.hk_ist === 0) &&
                (!m.hk_kal || m.hk_kal === 0),
        );
        if (candidates.length === 0) {
            toast.info("Keine passenden Titel in der aktuellen Ansicht.");
            return;
        }
        if (
            !window.confirm(
                `${candidates.length} sichtbare Titel als HK bewusst leer bestätigen?\n\n` +
                    'Diese Titel verschwinden aus dem Fehlerprotokoll und bleiben mit Bilanzwert 0 € im Inventar sichtbar.',
            )
        ) {
            return;
        }
        setSaving(true);
        try {
            const ids = candidates.map((m) => m.id);
            const r = await api.patch("/movements/hk-confirm-null", {
                tenant_id: activeTenant.tenant_id,
                movement_ids: ids,
                confirmed: true,
            });
            toast.success(`${r.data.aktualisiert} Titel als HK bewusst leer bestätigt`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Massen-Aktion fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    /** Excel-Export der offenen HK-Titel (zur Weiterleitung an Mitarbeiter). */
    const exportOffeneHkExcel = async () => {
        if (!activeTenant) return;
        try {
            const t = await api.post("/admin/download-token", {
                purpose: "hk-pflege-xlsx",
                tenant_id: activeTenant.tenant_id,
                zeitraum,
            });
            const token = t.data?.token;
            const ttl = t.data?.expires_in || 600;
            if (!token) throw new Error("Kein Download-Token erhalten");
            const url =
                `${process.env.REACT_APP_BACKEND_URL}/api/export/hk-pflege-token.xlsx` +
                `?token=${encodeURIComponent(token)}` +
                `&tenant_id=${encodeURIComponent(activeTenant.tenant_id)}` +
                `&zeitraum=${encodeURIComponent(zeitraum)}`;
            const filename = `hk_pflege_offen_${activeTenant.tenant_id}_${zeitraum}.xlsx`;
            setDlDialog({ open: true, url, filename, expiresInSec: ttl });
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Excel-Export der offenen HK-Titel fehlgeschlagen",
            );
        }
    };

    const save = async () => {
        if (!dirtyCount) {
            toast.info("Keine Änderungen zu speichern");
            return;
        }
        setSaving(true);
        try {
            const updates = Object.entries(dirty).map(([id, vals]) => {
                const out = { movement_id: id };
                if (vals.hk_ist !== undefined) out.hk_ist = parseHk(vals.hk_ist);
                if (vals.hk_kal !== undefined) out.hk_kal = parseHk(vals.hk_kal);
                return out;
            });
            const r = await api.patch("/movements/hk-bulk", {
                tenant_id: activeTenant.tenant_id,
                updates,
            });
            toast.success(`${r.data.aktualisiert} Bewegungen aktualisiert`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daten</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                    HK-Pflege
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                    Pflege fehlender Herstellkosten (HK Ist / HK Kal) direkt in der Liste.
                    Änderungen werden gesammelt und mit einem Klick gespeichert.
                </p>
            </div>

            <Card className="p-4 bg-surface1 border-border">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs">Suche (ISBN, Titel)</Label>
                        <div className="relative mt-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="z.B. 978-…"
                                className="pl-9"
                                data-testid="hk-search-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Zeitraum</Label>
                        <Input
                            value={zeitraum}
                            onChange={(e) => setZeitraum(e.target.value)}
                            className="mt-1 w-[120px]"
                            data-testid="hk-period-input"
                        />
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                        <Switch
                            checked={onlyMissing}
                            onCheckedChange={setOnlyMissing}
                            id="only-missing"
                            data-testid="hk-only-missing"
                        />
                        <Label htmlFor="only-missing" className="text-sm cursor-pointer">
                            Nur ohne HK-Wert
                        </Label>
                    </div>
                    <Button
                        onClick={exportOffeneHkExcel}
                        disabled={!activeTenant}
                        variant="outline"
                        className="border-border"
                        title="Alle offenen Titel ohne HK-Wert als Excel exportieren (zum Weitergeben an Mitarbeiter)"
                        data-testid="hk-export-offen-btn"
                    >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Als Excel für Mitarbeiter
                    </Button>
                    <Button
                        onClick={bulkConfirmVisible}
                        disabled={isGuest || saving}
                        variant="outline"
                        className="border-border"
                        title='Alle sichtbaren Titel ohne HK als bewusst leer bestätigen'
                        data-testid="hk-bulk-confirm-null-btn"
                    >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        HK ø bestätigen (sichtbare)
                    </Button>
                    <Button
                        onClick={save}
                        disabled={isGuest || saving || dirtyCount === 0}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="hk-save-btn"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? "Speichere …" : `Speichern${dirtyCount ? ` (${dirtyCount})` : ""}`}
                    </Button>
                </div>

                <div className="mt-3 text-xs text-muted-foreground flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <Wrench className="h-3.5 w-3.5" />
                        <span>{filtered.length} Position{filtered.length !== 1 ? "en" : ""}</span>
                    </div>
                    {(() => {
                        const cn = filtered.filter((m) => m.hk_confirmed_null).length;
                        if (cn === 0) return null;
                        return (
                            <div className="flex items-center gap-1.5 text-emerald-400" data-testid="hk-confirmed-null-count">
                                <CheckSquare className="h-3.5 w-3.5" />
                                <span>{cn} Titel als &bdquo;HK bewusst leer&ldquo; bestätigt</span>
                            </div>
                        );
                    })()}
                    {search.trim() && onlyMissing && (
                        <div className="flex items-center gap-1.5 text-tenant">
                            <Search className="h-3.5 w-3.5" />
                            <span>Suche aktiv — &bdquo;Nur ohne HK&ldquo; wird ignoriert, alle Treffer werden angezeigt</span>
                        </div>
                    )}
                    {dirtyCount > 0 && (
                        <div className="flex items-center gap-1.5 text-yellow-400">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span>
                                {dirtyCount} ungespeicherte Änderung{dirtyCount !== 1 ? "en" : ""} · Lagerwerte in
                                <span className="italic"> kursiv-gelb</span> sind Vorschau
                            </span>
                        </div>
                    )}
                </div>
            </Card>

            <Card className="bg-surface1 border-border overflow-hidden">
                <div className="overflow-auto max-h-[70vh]">
                    <Table>
                        <TableHeader className="sticky top-0 bg-surface1 z-10">
                            <TableRow>
                                <TableHead className="w-[150px]">ISBN</TableHead>
                                <TableHead>Haupttitel</TableHead>
                                <TableHead className="w-[110px]">ET</TableHead>
                                <TableHead className="w-[110px] text-right">Bestand</TableHead>
                                <TableHead className="w-[140px]">HK Kal</TableHead>
                                <TableHead className="w-[140px]">HK Ist</TableHead>
                                <TableHead className="w-[100px] text-center" title="HK bewusst leer bestätigt">HK ø</TableHead>
                                <TableHead className="w-[130px] text-right">Lagerwert</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody data-testid="hk-table-body">
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        Lade …
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                        Keine Bewegungen für diese Auswahl.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                filtered.map((m) => {
                                    const isDirty = !!dirty[m.id];
                                    return (
                                        <TableRow
                                            key={m.id}
                                            className={isDirty ? "bg-yellow-500/5" : ""}
                                            data-testid={`hk-row-${m.isbn}`}
                                        >
                                            <TableCell className="font-mono text-xs">{m.isbn}</TableCell>
                                            <TableCell className="max-w-[280px] truncate" title={m.haupttitel}>
                                                {m.haupttitel}
                                            </TableCell>
                                            <TableCell
                                                className="text-xs text-muted-foreground tabular whitespace-nowrap"
                                                title={m.et || ""}
                                            >
                                                {formatEt(m)}
                                            </TableCell>
                                            <TableCell className="text-right tabular text-xs">
                                                {(m.lager_oe || 0) + (m.lager_de || 0)}
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={getDisplayVal(m, "hk_kal")}
                                                    onChange={(e) => setVal(m.id, "hk_kal", e.target.value)}
                                                    disabled={isGuest}
                                                    className="h-8 tabular"
                                                    placeholder="—"
                                                    data-testid={`hk-kal-${m.isbn}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={getDisplayVal(m, "hk_ist")}
                                                    onChange={(e) => setVal(m.id, "hk_ist", e.target.value)}
                                                    disabled={isGuest}
                                                    className="h-8 tabular"
                                                    placeholder="—"
                                                    data-testid={`hk-ist-${m.isbn}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={!!m.hk_confirmed_null}
                                                    disabled={isGuest || ((m.hk_ist || 0) > 0) || ((m.hk_kal || 0) > 0)}
                                                    onChange={(e) => toggleConfirmNull(m.id, e.target.checked)}
                                                    title={((m.hk_ist || 0) > 0 || (m.hk_kal || 0) > 0) ? "Nur möglich, wenn beide HK-Felder leer sind" : "HK bewusst leer bestätigen"}
                                                    className="h-4 w-4 cursor-pointer disabled:opacity-30"
                                                    data-testid={`hk-null-${m.isbn}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right tabular text-xs">
                                                {(() => {
                                                    const preview = getPreviewLagerwert(m);
                                                    const original = m.lagerwert || 0;
                                                    const changed = isDirty && preview !== null && Math.abs(preview - original) > 0.001;
                                                    if (changed) {
                                                        return (
                                                            <span
                                                                className="italic text-yellow-300"
                                                                title="Vorschau – noch nicht gespeichert"
                                                            >
                                                                {eur(preview)}
                                                            </span>
                                                        );
                                                    }
                                                    return eur(original);
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(v) => setDlDialog((d) => ({ ...d, open: v }))}
                url={dlDialog.url}
                filename={dlDialog.filename}
                expiresInSec={dlDialog.expiresInSec}
                title="HK-Pflege · Offene Titel exportieren"
                description="Excel-Datei mit allen Titeln ohne HK-Wert. Die gelben Spalten sind zum Ausfüllen durch die Mitarbeiter markiert."
            />
        </div>
    );
}
